import { useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  TextFilterModule,
  ValidationModule,
  colorSchemeDark,
  themeQuartz,
} from 'ag-grid-community';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getColumnDefs, getStatRowId, transformStatData } from './stat.utils';
import { StatStatus } from './StatStatus';
import './ag-grid-custom.css';

// Register only what the grid actually uses — AllCommunityModule pulls in
// every feature (editors, row drag, infinite row model, …) we never render.
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  PaginationModule,
  NumberFilterModule,
  TextFilterModule,
  CellStyleModule,
  ColumnAutoSizeModule,
  ...(import.meta.env.DEV ? [ValidationModule] : []),
]);

/**
 * ag-grid v33+ Theming API. The legacy `ag-theme-alpine` stylesheet plus an
 * `ag-theme-*` class is a different, mutually exclusive mechanism — the grid
 * strips those classes off its root when a theme object is present.
 */
const gridTheme = themeQuartz.withPart(colorSchemeDark).withParams({
  backgroundColor: 'var(--color-surface)',
  foregroundColor: 'var(--color-fg)',
  headerBackgroundColor: 'var(--color-surface-raised)',
  headerTextColor: 'var(--color-fg-muted)',
  oddRowBackgroundColor: 'rgba(255, 255, 255, 0.02)',
  borderColor: 'var(--color-line)',
  rowHoverColor: 'var(--color-accent-soft)',
  selectedRowBackgroundColor: 'var(--color-accent-soft)',
  accentColor: 'var(--color-accent)',
  fontFamily: 'inherit',
  fontSize: '12px',
  headerFontSize: '11.5px',
  headerFontWeight: 600,
  rowHeight: 32,
  headerHeight: 36,
  cellHorizontalPadding: 8,
  borderRadius: 'var(--radius-md)',
  wrapperBorder: false,
});

export default function StatGrid({ stat, filters, isFetching, error, hasData, onDrillDown }) {
  const { timezone, interval, excludeNoInstalls } = filters;
  const gridRef = useRef();
  const wrapperRef = useRef(null);

  const { isMobile: isSmallScreen, isTiny: isVerySmallScreen } = useIsMobile();
  const gridHeight = isSmallScreen ? 400 : 500;

  const rowData = useMemo(
    () => transformStatData(stat, excludeNoInstalls, timezone),
    [stat, excludeNoInstalls, timezone],
  );

  const columnDefs = useMemo(
    () => getColumnDefs(isSmallScreen, timezone, interval),
    [isSmallScreen, timezone, interval],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: isSmallScreen ? 80 : 100,
      flex: 1,
      cellStyle: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
    }),
    [isSmallScreen],
  );

  // Refit columns when the grid's own box changes size. A window 'resize'
  // listener fires far more often than the grid actually changes width, and
  // sizeColumnsToFit forces a layout pass every time.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => gridRef.current?.api?.sizeColumnsToFit());
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  // Only take over the viewport on the very first load or a hard failure.
  // Refetches keep the previous table on screen behind a thin progress bar, so
  // changing a filter no longer blanks the page.
  if (!hasData && (isFetching || error)) {
    return <StatStatus status={error ? 'failed' : 'loading'} error={error} height={gridHeight} />;
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-card-line border-t-card-line-top bg-surface shadow-soft transition-shadow hover:shadow-medium">
      {isFetching && (
        <div
          className="absolute inset-x-0 top-0 z-[2] h-0.5 overflow-hidden bg-accent-soft"
          role="status"
          aria-label="Refreshing statistics"
        >
          <div className="h-full w-[30%] animate-indeterminate bg-accent" />
        </div>
      )}
      <div
        ref={wrapperRef}
        className={`w-full transition-opacity ${isFetching ? 'opacity-60' : 'opacity-100'}`}
        style={{ height: isSmallScreen ? 'auto' : gridHeight }}
      >
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(params) => getStatRowId(params.data)}
          pagination={true}
          defaultColDef={defaultColDef}
          onGridReady={(params) => params.api.sizeColumnsToFit()}
          onRowClicked={
            onDrillDown
              ? ({ data }) => onDrillDown({ project: data.project, campaignId: data.campaign_id })
              : undefined
          }
          paginationPageSize={isVerySmallScreen ? 15 : isSmallScreen ? 25 : 50}
          paginationPageSizeSelector={isVerySmallScreen ? [15, 30, 50] : [25, 50, 100, 200]}
          animateRows={true}
          domLayout={isSmallScreen ? 'autoHeight' : 'normal'}
          suppressColumnVirtualisation={isSmallScreen}
          suppressRowVirtualisation={isSmallScreen}
          enableCellTextSelection={true}
          suppressMovableColumns={isSmallScreen}
          overlayNoRowsTemplate="No rows match the current filters"
        />
      </div>
    </div>
  );
}
